/**
 * THE THINGS THEY CHECK FIRST (ops #86, `docs/plan-coach-review.md` section 4): the third question of
 * the coach review's pre-clean form, asked at booking beside parking and the way in. At most three
 * lines, each one thing the household said they look at first when they walk in, and at most 400
 * characters in all. Stored as one text on the home (`sites.checks_first`), frozen onto the sheet at
 * its first open, and drawn on the cleaner's sheet right under the do-not list.
 *
 * One definition, two consumers: the Worker's doors refuse what breaks the rule, and the screens draw
 * three boxes and join them the same way, so what the office typed is what the server keeps.
 */

/** Three things, one a line. A fourth is refused at the door. */
export const CHECKS_FIRST_MAX_LINES = 3;

/** The whole text, the newlines between the lines included. */
export const CHECKS_FIRST_MAX_CHARACTERS = 400;

/**
 * THE TEXT AS IT IS KEPT: each line trimmed, the empty ones dropped, joined by a newline, and null when
 * nothing is left, so three empty boxes clear the column rather than store an empty string.
 */
export function normaliseChecksFirst(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return null;
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
  return lines.length === 0 ? null : lines.join('\n');
}

/** The kept text back into its lines, one box each, always `CHECKS_FIRST_MAX_LINES` long or longer. */
export function checksFirstLines(text: string | null | undefined): string[] {
  const lines = normaliseChecksFirst(text)?.split('\n') ?? [];
  while (lines.length < CHECKS_FIRST_MAX_LINES) lines.push('');
  return lines;
}

/** What is wrong with a kept text, in words for the office, or null when nothing is. */
export function checksFirstProblem(text: string | null): string | null {
  if (text === null) return null;
  if (text.split('\n').length > CHECKS_FIRST_MAX_LINES) {
    return 'says at most three things, one a line';
  }
  if (text.length > CHECKS_FIRST_MAX_CHARACTERS) {
    return `is at most ${CHECKS_FIRST_MAX_CHARACTERS} characters`;
  }
  return null;
}
